import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Fragment, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { RecurringJob, RecurringStatus } from "../../../api/client.ts";
import ScopeToggle from "../../filters/ScopeToggle.tsx";
import Panel from "../../Panel.tsx";
import StatusIcon from "../../StatusIcon.tsx";
import JobName from "./JobName.tsx";
import { jobTitle, recurringStatus } from "./recurringFormat.ts";

type Span = "day" | "week";

/** The last 24 hours one cell an hour; the week one cell per six hours. */
const SPANS: Record<Span, { hours: number; per: number; axis: [string, number][] }> = {
  day: { hours: 24, per: 1, axis: [["24 h ago", 0], ["12 h", 0.5], ["6 h", 0.75], ["now", 1]] },
  week: { hours: 168, per: 6, axis: [["7 days ago", 0], ["4 d", 3 / 7], ["2 d", 5 / 7], ["now", 1]] },
};

/** Worst first; the backend's own order (`recurring._SEVERITY`) plus `off` last. */
const SEVERITY: Record<RecurringStatus, number> = {
  failing: 6,
  late: 5,
  "running long": 4,
  "no runs": 3,
  ok: 2,
  stopped: 1,
  off: 0,
};

const tint = (color: string, opacity: number) => `rgba(var(--mui-palette-${color}Channel) / ${opacity})`;
const CELL = {
  ran: tint("success-main", 0.7),
  partial: "var(--mui-palette-warning-main)",
  failed: "var(--mui-palette-error-main)",
  idle: tint("text-primary", 0.06),
  off: tint("text-primary", 0.14),
};

const NAME_WIDTH = 248;
const CATEGORY_WIDTH = 152;
const STATUS_WIDTH = 100;

interface Cell {
  start: Date;
  ran: number;
  failed: number;
}

function cells(job: RecurringJob, span: Span): Cell[] {
  const { hours, per } = SPANS[span];
  const hourly = job.hourly.slice(-hours);
  const out: Cell[] = [];
  for (let i = 0; i < hourly.length; i += per) {
    const chunk = hourly.slice(i, i + per);
    out.push({
      start: new Date(chunk[0].hour),
      ran: chunk.reduce((sum, h) => sum + h.ran, 0),
      failed: chunk.reduce((sum, h) => sum + h.failed, 0),
    });
  }
  return out;
}

function cellColor(job: RecurringJob, cell: Cell): string {
  if (cell.failed) return cell.ran ? CELL.partial : CELL.failed;
  if (cell.ran) return CELL.ran;
  return job.switch_on ? CELL.idle : CELL.off;
}

function cellTitle(cell: Cell, per: number): string {
  const end = new Date(cell.start.getTime() + per * 3_600_000);
  const time = (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const day = per > 1 ? `${cell.start.toLocaleDateString([], { weekday: "short" })} ` : "";
  const runs = cell.ran + cell.failed;
  const what = runs
    ? `${runs} ${runs === 1 ? "run" : "runs"}${cell.failed ? `, ${cell.failed} failed` : ""}`
    : "No runs";
  return `${day}${time(cell.start)}–${time(end)} · ${what}`;
}

/** A job that needs a look: failing, late, running long or never ran. */
function needsLook(job: RecurringJob): boolean {
  const kind = recurringStatus(job.status).kind;
  return kind === "error" || kind === "attention";
}

/** Jobs that need a look first, then the rest by category, country and title. */
function order(a: RecurringJob, b: RecurringJob): number {
  const look = Number(needsLook(b)) - Number(needsLook(a));
  if (look) return look;
  if (needsLook(a)) {
    const worse = SEVERITY[b.status] - SEVERITY[a.status] || b.failures_24h - a.failures_24h;
    if (worse) return worse;
  }
  return (
    a.category_label.localeCompare(b.category_label) ||
    (a.country ?? "~").localeCompare(b.country ?? "~") ||
    jobTitle(a).localeCompare(jobTitle(b))
  );
}

/**
 * Every recurring job as a row of hourly cells (a week: six-hour cells): green
 * ran, orange some runs failed, red all failed. Jobs that need a look come
 * first, then the rest grouped by category, each name after the flag of the
 * country it syncs. A row opens the job on the Recurring tab.
 */
export default function JobHeatmapPanel({ jobs }: { jobs: RecurringJob[] }) {
  const [span, setSpan] = useState<Span>("day");
  const { per, axis } = SPANS[span];
  const rows = [...jobs].sort(order);

  return (
    <Panel
      title="Background jobs"
      count={jobs.length}
      subtitle="Each job's runs over time: the ones that need a look first, then by category"
      disablePadding
      action={
        <ScopeToggle<Span>
          value={span}
          onChange={setSpan}
          aria-label="Time span"
          options={[
            { value: "day", label: "24 h" },
            { value: "week", label: "7 days" },
          ]}
        />
      }
    >
      <Box sx={{ px: 2, pt: 1.25, pb: 1.5 }}>
        <Stack direction="row" sx={{ gap: 1.5, mb: 1, flexWrap: "wrap" }}>
          <Swatch color={CELL.ran} label="Ran" />
          <Swatch color={CELL.partial} label="Some runs failed" />
          <Swatch color={CELL.failed} label="All runs failed" />
          <Swatch color={CELL.idle} label="Not due" />
          <Swatch color={CELL.off} label="Switched off" />
        </Stack>
        {rows.map((job, i) => {
          const prev = i > 0 ? rows[i - 1] : null;
          const look = needsLook(job);
          // A rule under the jobs that need a look; a gap between categories.
          const rule = prev != null && needsLook(prev) && !look;
          const newCategory = !look && (prev == null || needsLook(prev) || prev.category !== job.category);
          return (
            <Fragment key={job.name}>
              {rule && <Box sx={{ borderTop: 1, borderColor: "divider", my: 1 }} />}
              {newCategory && !rule && prev != null && <Box sx={{ height: 6 }} />}
              <JobRow job={job} span={span} per={per} showCategory={look || newCategory} />
            </Fragment>
          );
        })}
        <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
          <Box sx={{ width: NAME_WIDTH + CATEGORY_WIDTH, flexShrink: 0 }} />
          <Box sx={{ flex: 1, position: "relative", height: 16 }}>
            {axis.map(([label, at]) => (
              <Typography
                key={label}
                variant="caption"
                sx={{
                  position: "absolute",
                  left: `${at * 100}%`,
                  transform: at === 0 ? undefined : at === 1 ? "translateX(-100%)" : "translateX(-50%)",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>
          <Box sx={{ width: STATUS_WIDTH, flexShrink: 0 }} />
        </Box>
      </Box>
    </Panel>
  );
}

function JobRow({
  job,
  span,
  per,
  showCategory,
}: {
  job: RecurringJob;
  span: Span;
  per: number;
  showCategory: boolean;
}) {
  const status = recurringStatus(job.status);
  const look = needsLook(job);
  const why = job.chains.find((c) => c.status === job.status && c.last_error)?.last_error;
  const row = cells(job, span);
  // Late or stuck shows on the cell it is due in, the current one.
  const waiting = job.status === "late" || job.status === "running long";
  return (
    <Box
      component={RouterLink}
      to={`/platform/recurring?job=${encodeURIComponent(job.name)}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: 15,
        color: "inherit",
        textDecoration: "none",
        borderRadius: 0.5,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{
          width: NAME_WIDTH,
          flexShrink: 0,
          display: "flex",
          lineHeight: 1,
          color: job.switch_on ? "text.primary" : "text.disabled",
          fontWeight: look ? 600 : undefined,
        }}
      >
        <JobName country={job.country} reserve>{jobTitle(job)}</JobName>
      </Typography>
      <Typography
        variant="caption"
        noWrap
        sx={{ width: CATEGORY_WIDTH, flexShrink: 0, lineHeight: 1, color: "text.secondary" }}
      >
        {showCategory ? job.category_label : ""}
      </Typography>
      <Box sx={{ flex: 1, display: "flex", gap: "2px", height: 11 }}>
        {row.map((cell, i) => {
          const due = waiting && i === row.length - 1 && !cell.ran && !cell.failed;
          return (
            <Box
              key={cell.start.getTime()}
              title={cellTitle(cell, per)}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: "2px",
                bgcolor: due ? "transparent" : cellColor(job, cell),
                ...(due && { outline: "1.5px solid var(--mui-palette-warning-main)", outlineOffset: "-1.5px" }),
              }}
            />
          );
        })}
      </Box>
      <Tooltip title={why ?? ""} disableHoverListener={!why}>
        <Box sx={{ width: STATUS_WIDTH, flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusIcon kind={status.kind} size={13} />
          <Typography variant="caption" noWrap sx={{ lineHeight: 1, color: "text.secondary" }}>
            {status.label}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box component="span" sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }} />
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
    </Box>
  );
}
